// ── Sprint Planning: two-panel drag layout with capacity tracking ──

import { api } from '../api.js';
import { checkAuth, getUser } from '../auth.js';
import { showToast, openModal, closeModal } from '../ui.js';
import { escapeHtml, getTypeIcon, debounce } from '../utils.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import { openTaskDetail, closeTaskDetail } from '../components/task-detail.js';
import { findInsertBefore, dropIndex } from '../lib/drag-order.js';
import { loadProject } from '../lib/project.js';
import { groupLabelPlural, groupLabelSingular, isGeneratedGroupName } from '../lib/task-group-label.js';

const DRAG_THRESHOLD = 5;
const VELOCITY_KEY = 'tipatask_velocity_';

let state = {
  projectId: null,
  project: null,
  tasks: [],
  sprints: [],
  selectedSprintId: null,
  searchQuery: '',
  drag: null,
};

function resetState() {
  state = {
    projectId: null, project: null, tasks: [], sprints: [],
    selectedSprintId: null, searchQuery: '', drag: null,
  };
}

// (C1395) — this page never fetched the project before, so every group noun here was a
// hardcoded "Sprint" regardless of task_group_label. Mirrors board.js's own
// groupLabelSingular/Plural(state.project && state.project.task_group_label) call shape.
function singular() {
  return groupLabelSingular(state.project && state.project.task_group_label);
}
function plural() {
  return groupLabelPlural(state.project && state.project.task_group_label);
}
// This selector shows a sprint's raw name when it's genuinely custom (unlike
// sprint-board.js's lane titles, it never prefixes a custom name with the noun) — only
// a boilerplate auto-name ("Sprint N", written regardless of task_group_label) resolves
// to the live noun instead of echoing "Sprint" verbatim.
function selectorLabel(s) {
  return isGeneratedGroupName(s.name, s.number) ? `${singular()} ${s.number}` : s.name;
}

// ── Helpers ──

function getSelectedSprint() {
  return state.sprints.find(s => s.id === state.selectedSprintId) || null;
}

function getBacklogTasks() {
  let tasks = state.tasks.filter(t => t.priority === 0);
  if (state.searchQuery) {
    const q = state.searchQuery.toLowerCase();
    tasks = tasks.filter(t => t.title.toLowerCase().includes(q));
  }
  return tasks.sort((a, b) => b.display_order - a.display_order);
}

function getSprintTasks() {
  const sprint = getSelectedSprint();
  if (!sprint) return [];
  return state.tasks.filter(t => t.priority === sprint.number)
    .sort((a, b) => b.display_order - a.display_order);
}

function getVelocity() {
  const v = localStorage.getItem(VELOCITY_KEY + state.projectId);
  const n = parseInt(v, 10);
  return isNaN(n) ? null : n;
}

function setVelocity(value) {
  const n = parseInt(value, 10);
  if (isNaN(n) || n <= 0) {
    localStorage.removeItem(VELOCITY_KEY + state.projectId);
  } else {
    localStorage.setItem(VELOCITY_KEY + state.projectId, n);
  }
}

function sumPoints(tasks) {
  return tasks.reduce((s, t) => s + (t.story_points || 0), 0);
}

// ── Rendering ──

function renderPlanningItem(task) {
  const catClass = task.category === 'CODING' ? 'badge-coding' : 'badge-human';
  let html = `<div class="planning-item" data-task-key="${escapeHtml(task.task_key)}" data-priority="${task.priority}">`;
  html += `<span class="planning-drag-handle" title="Drag to move">&#10495;</span>`;
  html += getTypeIcon(task.type);
  html += `<a class="badge ${catClass} task-key-link" style="flex-shrink:0;" href="#/projects/${state.projectId}/tasks/${encodeURIComponent(task.task_key)}" draggable="false" title="Open task page">${escapeHtml(task.task_key)}</a>`;
  html += `<span class="planning-item-title">${escapeHtml(task.title)}</span>`;
  if (task.story_points != null) {
    html += `<span class="backlog-sp-pill">${task.story_points}</span>`;
  }
  html += `</div>`;
  return html;
}

function renderCapacityIndicator(committedPoints) {
  const velocity = getVelocity();
  if (!velocity) {
    return `<span class="planning-velocity-btn" id="planning-velocity-btn">Set velocity</span>`;
  }
  const ratio = committedPoints / velocity;
  const pct = Math.min(ratio * 100, 100);
  const colorClass = ratio > 1 ? 'planning-capacity-red'
    : ratio >= 0.8 ? 'planning-capacity-yellow'
    : 'planning-capacity-green';

  let html = `<div class="planning-capacity ${colorClass}">`;
  html += `<span class="planning-velocity-btn" id="planning-velocity-btn">${committedPoints} / ${velocity} pts</span>`;
  html += `<div class="planning-capacity-bar">`;
  html += `<div class="planning-capacity-fill" style="width:${pct}%"></div>`;
  html += `</div></div>`;
  return html;
}

function renderLeftPanel() {
  const tasks = getBacklogTasks();
  const pts = sumPoints(tasks);

  let html = `<div class="planning-panel" id="planning-left">`;
  html += `<div class="planning-panel-header"><h3>Product Backlog</h3><span class="planning-count">${tasks.length} item${tasks.length !== 1 ? 's' : ''}</span></div>`;
  html += `<input type="text" class="form-input planning-search" id="planning-search" placeholder="Search backlog..." value="${escapeHtml(state.searchQuery)}">`;
  html += `<div class="planning-items" id="backlog-items" data-panel="backlog">`;
  if (tasks.length === 0) {
    html += `<div class="empty text-muted" style="padding:1rem;font-size:0.85rem;">${state.searchQuery ? 'No matching items' : 'No backlog items'}</div>`;
  } else {
    html += tasks.map(renderPlanningItem).join('');
  }
  html += `</div>`;
  html += `<div class="planning-footer"><span>${pts} pts</span></div>`;
  html += `</div>`;
  return html;
}

function renderSprintSelector() {
  const active = state.sprints.filter(s => s.status === 'active');
  const closed = state.sprints.filter(s => s.status === 'closed');

  let html = `<select class="form-select" id="sprint-selector" style="flex:1;min-width:0;">`;
  if (!state.selectedSprintId && state.sprints.length > 0) {
    html += `<option value="" disabled selected>Select ${escapeHtml(singular().toLowerCase())}...</option>`;
  }
  if (!state.sprints.length) {
    html += `<option value="" disabled selected>No ${escapeHtml(plural().toLowerCase())}</option>`;
  }
  active.forEach(s => {
    const sel = s.id === state.selectedSprintId ? ' selected' : '';
    html += `<option value="${s.id}"${sel}>${escapeHtml(selectorLabel(s))}</option>`;
  });
  if (closed.length) {
    html += `<optgroup label="Closed">`;
    closed.forEach(s => {
      const sel = s.id === state.selectedSprintId ? ' selected' : '';
      html += `<option value="${s.id}"${sel}>${escapeHtml(selectorLabel(s))}</option>`;
    });
    html += `</optgroup>`;
  }
  html += `<option value="__new__">+ New ${escapeHtml(singular())}</option>`;
  html += `</select>`;
  return html;
}

function renderRightPanel() {
  const sprint = getSelectedSprint();
  const tasks = getSprintTasks();
  const pts = sumPoints(tasks);

  let html = `<div class="planning-panel" id="planning-right">`;
  html += `<div class="planning-panel-header">${renderSprintSelector()}</div>`;

  // Goal
  if (sprint) {
    const goal = sprint.goal || '';
    html += `<div class="planning-goal" id="planning-goal" title="Click to edit goal">${goal ? escapeHtml(goal) : `<span class="text-faint">Click to set ${escapeHtml(singular().toLowerCase())} goal...</span>`}</div>`;
  }

  html += `<div class="planning-items" id="sprint-items" data-panel="sprint">`;
  if (!sprint) {
    html += `<div class="empty text-muted" style="padding:1rem;font-size:0.85rem;">Select a ${escapeHtml(singular().toLowerCase())}</div>`;
  } else if (tasks.length === 0) {
    html += `<div class="empty text-muted" style="padding:1rem;font-size:0.85rem;">Drag items here to commit</div>`;
  } else {
    html += tasks.map(renderPlanningItem).join('');
  }
  html += `</div>`;

  html += `<div class="planning-footer">`;
  html += `<span>${pts} pts committed</span>`;
  html += renderCapacityIndicator(pts);
  html += `</div>`;
  html += `</div>`;
  return html;
}

function renderPlanning() {
  const container = document.getElementById('planning-container');
  if (!container) return;
  container.innerHTML = `<div class="planning-layout">${renderLeftPanel()}${renderRightPanel()}</div>`;
}

// ── Sprint actions ──

function handleSprintSelect(e) {
  const val = e.target.value;
  if (val === '__new__') {
    // Reset selector to previous
    const sel = document.getElementById('sprint-selector');
    if (sel) {
      const prev = state.selectedSprintId;
      const opt = sel.querySelector(`option[value="${prev}"]`);
      if (opt) opt.selected = true;
    }
    handleNewSprint();
    return;
  }
  state.selectedSprintId = parseInt(val, 10);
  renderPlanning();
}

function handleNewSprint() {
  const label = singular();
  const body = `
    <form id="new-sprint-form" style="display:flex;flex-direction:column;gap:0.75rem;">
      <input class="form-input" id="new-sprint-name" placeholder="${escapeHtml(label)} name" required maxlength="255" autofocus>
      <button type="submit" class="btn btn-primary">Create ${escapeHtml(label)}</button>
    </form>`;

  openModal(`New ${label}`, body, {
    onMount(overlay) {
      overlay.querySelector('#new-sprint-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const input = overlay.querySelector('#new-sprint-name');
        const name = input.value.trim();
        if (!name) return;
        input.disabled = true;
        try {
          const { sprint } = await api.post('/projects/' + state.projectId + '/sprints', { name });
          state.sprints.push(sprint);
          state.selectedSprintId = sprint.id;
          closeModal();
          renderPlanning();
          showToast(`${label} created`, 'success');
        } catch (err) {
          showToast(err.message || `Failed to create ${label.toLowerCase()}`, 'error');
          input.disabled = false;
        }
      });
    },
  });
}

async function handleGoalSave(newGoal) {
  const sprint = getSelectedSprint();
  if (!sprint) return;
  const old = sprint.goal;
  sprint.goal = newGoal;
  renderPlanning();
  try {
    await api.patch('/projects/' + state.projectId + '/sprints/' + sprint.id, { goal: newGoal });
  } catch (err) {
    sprint.goal = old;
    renderPlanning();
    showToast('Failed to update goal', 'error');
  }
}

function handleVelocityPrompt() {
  const current = getVelocity();
  const body = `
    <form id="velocity-form" style="display:flex;flex-direction:column;gap:0.75rem;">
      <label class="form-label">Team velocity (story points per ${escapeHtml(singular().toLowerCase())})</label>
      <input class="form-input" id="velocity-input" type="number" min="1" placeholder="e.g. 30" value="${current || ''}" autofocus>
      <div style="display:flex;gap:0.5rem;">
        <button type="submit" class="btn btn-primary" style="flex:1;">Save</button>
        ${current ? '<button type="button" class="btn btn-ghost" id="velocity-clear">Clear</button>' : ''}
      </div>
    </form>`;

  openModal('Set Velocity', body, {
    onMount(overlay) {
      overlay.querySelector('#velocity-form').addEventListener('submit', (e) => {
        e.preventDefault();
        const val = overlay.querySelector('#velocity-input').value;
        setVelocity(val);
        closeModal();
        renderPlanning();
      });
      const clearBtn = overlay.querySelector('#velocity-clear');
      if (clearBtn) {
        clearBtn.addEventListener('click', () => {
          setVelocity(null);
          closeModal();
          renderPlanning();
        });
      }
    },
  });
}

// ── Task detail ──

function openTaskDetailPanel(taskKey) {
  openTaskDetail(state.projectId, taskKey, {
    allTasks: state.tasks,
    onClose: async () => { await refetchTasks(); },
  });
}

// ── Refetch ──

async function refetchTasks() {
  try {
    const { tasks } = await api.get('/projects/' + state.projectId + '/tasks?top_level=true&type=story,task,bug');
    state.tasks.splice(0, state.tasks.length, ...(tasks || []));
  } catch (_) {}
  renderPlanning();
}

// ── Drag and drop ──

let onMouseDown, onMouseMove, onMouseUp;

function bindDragAndDrop() {
  onMouseDown = (e) => {
    const handle = e.target.closest('.planning-drag-handle');
    if (!handle) return;
    const item = handle.closest('.planning-item');
    if (!item) return;

    const rect = item.getBoundingClientRect();
    state.drag = {
      taskKey: item.dataset.taskKey,
      el: item,
      clone: null,
      placeholder: null,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      moved: false,
    };
    e.preventDefault();
  };

  onMouseMove = (e) => {
    const d = state.drag;
    if (!d) return;

    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    if (!d.moved) {
      if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
      d.moved = true;

      d.clone = d.el.cloneNode(true);
      d.clone.classList.add('planning-item-dragging');
      d.clone.style.width = d.el.offsetWidth + 'px';
      document.body.appendChild(d.clone);

      d.placeholder = document.createElement('div');
      d.placeholder.className = 'planning-item-placeholder';
      d.placeholder.style.height = d.el.offsetHeight + 'px';
      d.el.parentNode.insertBefore(d.placeholder, d.el);

      d.el.classList.add('planning-item-hidden');
    }

    d.clone.style.left = (e.clientX - d.offsetX) + 'px';
    d.clone.style.top = (e.clientY - d.offsetY) + 'px';

    const target = document.elementFromPoint(e.clientX, e.clientY);
    if (!target) return;
    const container = target.closest('.planning-items');

    document.querySelectorAll('.planning-items.drag-over')
      .forEach(el => el.classList.remove('drag-over'));

    if (!container) return;
    container.classList.add('drag-over');

    const items = [...container.querySelectorAll('.planning-item:not(.planning-item-hidden)')];
    const insertBefore = findInsertBefore(items, e.clientX, e.clientY, 'y');

    if (insertBefore) {
      container.insertBefore(d.placeholder, insertBefore);
    } else {
      container.appendChild(d.placeholder);
    }
  };

  onMouseUp = async (e) => {
    const d = state.drag;
    if (!d) return;

    document.querySelectorAll('.planning-items.drag-over')
      .forEach(el => el.classList.remove('drag-over'));

    if (!d.moved) {
      state.drag = null;
      return;
    }

    const container = d.placeholder?.closest('.planning-items');
    const panel = container ? container.dataset.panel : null;

    // Must run BEFORE the cleanup below removes 'planning-item-hidden' from d.el — dropIndex()
    // counts elements NOT carrying that class, so a hidden-then-unhidden dragged original
    // would otherwise start counting itself (see lib/drag-order.js).
    const placeholderIndex = container
      ? dropIndex(container, d.placeholder, { selector: '.planning-item', hiddenClass: 'planning-item-hidden' })
      : 0;

    // Cleanup DOM
    if (d.clone) d.clone.remove();
    if (d.placeholder) d.placeholder.remove();
    d.el.classList.remove('planning-item-hidden');

    const task = state.tasks.find(t => t.task_key === d.taskKey);
    state.drag = null;

    if (!task || !panel) return;

    // Determine target priority
    const sprint = getSelectedSprint();
    let targetPriority;
    if (panel === 'sprint') {
      if (!sprint) return;
      targetPriority = sprint.number;
    } else {
      targetPriority = 0;
    }

    // Build new ordered list for target
    const laneTasks = state.tasks
      .filter(t => t.priority === targetPriority && t.task_key !== task.task_key)
      .sort((a, b) => b.display_order - a.display_order);

    const insertAt = Math.min(placeholderIndex, laneTasks.length);
    laneTasks.splice(insertAt, 0, task);

    // Calculate patches
    const patches = [];
    laneTasks.forEach((t, i) => {
      const newOrder = (laneTasks.length - i) * 10;
      const priorityChanged = t.task_key === task.task_key && t.priority !== targetPriority;
      const orderChanged = t.display_order !== newOrder;
      if (priorityChanged || orderChanged) {
        const patch = { display_order: newOrder };
        if (priorityChanged) patch.priority = targetPriority;
        patches.push({ taskKey: t.task_key, patch });
      }
    });

    if (patches.length === 0) return;

    // Optimistic update
    task.priority = targetPriority;
    laneTasks.forEach((t, i) => { t.display_order = (laneTasks.length - i) * 10; });
    renderPlanning();

    try {
      const results = await Promise.all(patches.map(({ taskKey, patch }) =>
        api.patch(`/projects/${state.projectId}/tasks/${taskKey}`, patch)
      ));
      // C1464 — dropping an objective parent into a sprint can cascade its children
      // (and its own priority) to sprints beyond this drag's own optimistic update —
      // refetch so the board reflects the server-computed schedule instead of going stale.
      if (results.some(r => r && r.children_rescheduled)) {
        await refetchTasks();
      }
    } catch (err) {
      showToast('Failed to save', 'error');
      await refetchTasks();
    }
  };

  document.addEventListener('mousedown', onMouseDown);
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
}

function cleanupDrag() {
  if (state.drag) {
    if (state.drag.clone) state.drag.clone.remove();
    if (state.drag.placeholder) state.drag.placeholder.remove();
    if (state.drag.el) state.drag.el.classList.remove('planning-item-hidden');
    state.drag = null;
  }
  document.querySelectorAll('.planning-items.drag-over')
    .forEach(el => el.classList.remove('drag-over'));
  if (onMouseDown) document.removeEventListener('mousedown', onMouseDown);
  if (onMouseMove) document.removeEventListener('mousemove', onMouseMove);
  if (onMouseUp) document.removeEventListener('mouseup', onMouseUp);
}

// ── Events ──

let containerHandler = null;
let searchHandler = null;

function bindEvents() {
  const container = document.getElementById('planning-container');
  if (!container) return;

  containerHandler = (e) => {
    // Sprint selector
    if (e.type === 'change' && e.target.id === 'sprint-selector') {
      handleSprintSelect(e);
      return;
    }

    if (e.type === 'click') {
      // Velocity button
      if (e.target.closest('#planning-velocity-btn')) {
        handleVelocityPrompt();
        return;
      }

      // Goal click → inline edit
      const goalEl = e.target.closest('#planning-goal');
      if (goalEl && !goalEl.querySelector('input')) {
        const sprint = getSelectedSprint();
        const currentGoal = sprint ? (sprint.goal || '') : '';
        goalEl.innerHTML = `<input class="form-input planning-goal-input" id="planning-goal-input" value="${escapeHtml(currentGoal)}" placeholder="${escapeHtml(singular())} goal...">`;
        const input = goalEl.querySelector('#planning-goal-input');
        input.focus();
        input.addEventListener('blur', () => { handleGoalSave(input.value.trim()); });
        input.addEventListener('keydown', (ev) => {
          if (ev.key === 'Enter') { ev.preventDefault(); input.blur(); }
          if (ev.key === 'Escape') { renderPlanning(); }
        });
        return;
      }

      // Item click → task detail (exclude drag handle)
      const item = e.target.closest('.planning-item');
      if (item && !e.target.closest('.planning-drag-handle, a')) {
        openTaskDetailPanel(item.dataset.taskKey);
        return;
      }
    }
  };

  container.addEventListener('click', containerHandler);
  container.addEventListener('change', containerHandler);

  // Debounced search
  searchHandler = debounce((e) => {
    state.searchQuery = e.target.value.trim();
    // Re-render left panel only
    const leftItems = document.getElementById('backlog-items');
    const leftHeader = document.querySelector('#planning-left .planning-count');
    const leftFooter = document.querySelector('#planning-left .planning-footer');
    if (!leftItems) return;

    const tasks = getBacklogTasks();
    const pts = sumPoints(tasks);

    if (tasks.length === 0) {
      leftItems.innerHTML = `<div class="empty text-muted" style="padding:1rem;font-size:0.85rem;">${state.searchQuery ? 'No matching items' : 'No backlog items'}</div>`;
    } else {
      leftItems.innerHTML = tasks.map(renderPlanningItem).join('');
    }
    if (leftHeader) leftHeader.textContent = `${tasks.length} item${tasks.length !== 1 ? 's' : ''}`;
    if (leftFooter) leftFooter.innerHTML = `<span>${pts} pts</span>`;
  }, 200);

  container.addEventListener('input', (e) => {
    if (e.target.id === 'planning-search') searchHandler(e);
  });
}

function cleanupEvents() {
  const container = document.getElementById('planning-container');
  if (container && containerHandler) {
    container.removeEventListener('click', containerHandler);
    container.removeEventListener('change', containerHandler);
  }
  containerHandler = null;
  searchHandler = null;
}

// ── Page entry ──

export async function sprintPlanningPage(params) {
  if (!(await checkAuth())) return;
  resetState();
  state.projectId = params.id;

  initLayout(getUser());
  // viewLabel set generically here — the project fetch below hasn't resolved yet
  // (C1395, same ordering board.js already lives with for its header button).
  updateLayoutContext({ projectId: params.id, projectName: null, activeView: 'planning', viewLabel: 'Sprint Planning' });

  const content = getContentEl();
  content.innerHTML = `
    <div class="page-header">
      <h2 id="planning-title">Sprint Planning</h2>
    </div>
    <div id="planning-container"><div class="empty"><span class="spinner"></span></div></div>`;

  try {
    const [tasksRes, sprintsRes, project] = await Promise.all([
      api.get('/projects/' + params.id + '/tasks?top_level=true&type=story,task,bug'),
      api.get('/projects/' + params.id + '/sprints'),
      // (C1395) task_group_label for every noun on this page — own fail-open leg
      // (loadProject already fails open to null), same shape as board.js's leg.
      loadProject(params.id),
    ]);
    state.tasks = tasksRes.tasks || [];
    state.sprints = sprintsRes.sprints || [];
    state.project = project;

    const titleEl = document.getElementById('planning-title');
    if (titleEl) titleEl.textContent = `${plural()} Planning`;
    updateLayoutContext({ projectId: params.id, projectName: project?.name || null, activeView: 'planning', viewLabel: `${plural()} Planning` });

    // Auto-select first active sprint
    const active = state.sprints.find(s => s.status === 'active');
    if (active) state.selectedSprintId = active.id;

    renderPlanning();
    bindEvents();
    bindDragAndDrop();
  } catch (err) {
    document.getElementById('planning-container').innerHTML =
      `<div class="empty text-muted">Failed to load planning data</div>`;
  }

  return () => {
    closeTaskDetail();
    cleanupDrag();
    cleanupEvents();
    closeModal();
    resetState();
  };
}
